import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SubAccountLayout from '../../components/SubAccountLayout';
import { useSubAccount } from '../../components/SubAccountLayout';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import {
  Bot,
  MessageSquare,
  Plus,
  Search,
  MoreVertical,
  Play,
  Pause,
  Trash2,
  Copy,
  Edit,
  ChevronRight,
  ChevronLeft,
  Phone,
  Globe,
  Zap,
  Clock,
  X,
  Mic,
  Sparkles,
  ArrowRight,
  Check,
  Loader2,
  FileText,
  Settings2,
  Volume2,
  PhoneIncoming,
  PhoneOutgoing,
  Wand2,
  Shield,
  Info,
  Database,
  Calendar,
  ListChecks,
  Palette,
  PenTool,
  Folder,
  FolderPlus,
  FolderOpen,
  Pencil,
  Tag
} from 'lucide-react';

// ─── CONSTANTS ───

const VOICE_OPTIONS = [
  { id: 'alloy', name: 'Alloy', gender: 'Neutral', accent: 'American', preview: 'Professional & Clear' },
  { id: 'echo', name: 'Echo', gender: 'Male', accent: 'American', preview: 'Deep & Confident' },
  { id: 'fable', name: 'Fable', gender: 'Male', accent: 'British', preview: 'Warm & Engaging' },
  { id: 'onyx', name: 'Onyx', gender: 'Male', accent: 'American', preview: 'Strong & Authoritative' },
  { id: 'nova', name: 'Nova', gender: 'Female', accent: 'American', preview: 'Friendly & Energetic' },
  { id: 'shimmer', name: 'Shimmer', gender: 'Female', accent: 'American', preview: 'Soft & Calming' },
];

const GROK_VOICE_OPTIONS = [
  { id: 'Eve', gender: 'Female', tone: 'Energetic', color: 'text-pink-400' },
  { id: 'Ara', gender: 'Female', tone: 'Warm', color: 'text-amber-400' },
  { id: 'Rex', gender: 'Male', tone: 'Confident', color: 'text-blue-400' },
  { id: 'Sal', gender: 'Neutral', tone: 'Smooth', color: 'text-emerald-400' },
  { id: 'Leo', gender: 'Male', tone: 'Authoritative', color: 'text-violet-400' },
];

const FUNCTION_OPTIONS = [
  { id: 'end_call', name: 'end_call', description: 'Gracefully end the current call', category: 'call' },
  { id: 'schedule_callback', name: 'schedule_callback', description: 'Schedule a callback at a specific time', category: 'call' },
  { id: 'check_availability', name: 'check_availability', description: 'Check calendar availability for appointments', category: 'calendar' },
  { id: 'book_appointment', name: 'book_appointment', description: 'Book an appointment on the calendar', category: 'calendar' },
  { id: 'reschedule_appointment', name: 'reschedule_appointment', description: 'Reschedule an existing appointment', category: 'calendar' },
  { id: 'cancel_appointment', name: 'cancel_appointment', description: 'Cancel an existing appointment', category: 'calendar' },
  { id: 'transfer_call', name: 'transfer_call', description: 'Transfer the call to another number or agent', category: 'call' },
  { id: 'add_tag', name: 'add_tag', description: 'Add a tag to the contact in LeadConnector', category: 'contact' },
  { id: 'remove_tag', name: 'remove_tag', description: 'Remove a tag from the contact', category: 'contact' },
  { id: 'update_contact', name: 'update_contact', description: 'Update contact information in LeadConnector', category: 'contact' },
  { id: 'add_note', name: 'add_note', description: 'Add a note to the contact record', category: 'contact' },
  { id: 'trigger_workflow', name: 'trigger_workflow', description: 'Trigger a LeadConnector workflow for the contact', category: 'automation' },
  { id: 'send_sms', name: 'send_sms', description: 'Send an SMS message to the contact', category: 'communication' },
  { id: 'update_opportunity_stage', name: 'update_opportunity_stage', description: 'Add contact to an opportunity pipeline stage', category: 'sales' },
];

const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'pt', name: 'Portuguese', flag: '🇧🇷' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
];


const DEFAULT_STYLE_GUARDRAILS = `Be Concise: Respond succinctly, addressing one topic at most.
Embrace Variety: Use diverse language and rephrasing to enhance clarity without repeating content.
Be Conversational: Use everyday language, making the chat feel like talking to a friend.
Be Proactive: Lead the conversation, often wrapping up with a question or next-step suggestion.
Avoid multiple questions in a single response.
Say "Um" in the most natural human way while speaking to sound like you make mistakes sometimes.
Get clarity: If the user only partially answers a question, or if the answer is unclear, keep asking to get clarity.
Use a colloquial way of referring to the date (like 'next Friday', 'tomorrow').
Use a colloquial way of referring to the time (like 'ten am', 'three thirty pm').
One question at a time: Ask only one question at a time, do not pack more topics into one response.
Acknowledge what they said with simple words like "Got it." "Sure." "Okay." "Right." "Understood." "Of course." "No problem." "Absolutely." "Will do." "Noted." "Alright." "Yep." "Gotcha." "On it." "Sounds good."`;

const DEFAULT_RESPONSE_GUIDELINES = `One question/action at a time.
Wait for user response before proceeding.
Use natural, conversational language and frequently ask open-ended questions to encourage detailed responses and ensure balanced interaction.
Adapt to their industry and communication style.
Never skip steps or rush through sequence.
Never Ask or confirm email.
Never mention steps.
Never schedule during role play.
All questions will be answered more in detail with a specialist.
Only provide 2 available time slots.
Search knowledge base for questions before answering. If its something you don't know, let them know we will go over all of that more in detail during the call.
Never repeat contacts name every single message.
If they ask if you know their contact info, confirm it with them.`;

// Friendly labels for AI model IDs shown in the agent list
const MODEL_LABELS = {
  'claude-opus-5.5': 'Opus 5.5',
  'claude-sonnet-5.5': 'Sonnet 5.5',
  'claude-sonnet-5': 'Sonnet 5',
  'claude-fable-5': 'Sonnet 5',
  'claude-sonnet-4.5': 'Sonnet 4.5',
  'claude-haiku-4.5': 'Haiku 4.5',
  'gpt-5.6-terra': 'GPT-5.6 Terra',
  'gpt-5.6-luna': 'GPT-5.6 Luna',
  'gpt-5.5': 'GPT-5.5',
  'gpt-5.4': 'GPT-5.4',
  'gpt-4o': 'GPT-4o',
  'gpt-4o-mini': 'GPT-4o Mini',
};
const modelLabel = (m) => MODEL_LABELS[m] || m || '—';
const fmtAgentDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
};

const AIAgentsContent = () => {
  const { workspaceId, subAccountId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { subAccount } = useSubAccount();
  const { isDarkMode } = useTheme();
  
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [showWizard, setShowWizard] = useState(searchParams.get('create') === 'true');
  const [showCreateChooser, setShowCreateChooser] = useState(false);
  const [creatingBlank, setCreatingBlank] = useState(false);

  // Folders
  const AGENT_FOLDER_COLORS = ['#14b8a6', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444', '#64748b'];
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState('all');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState(AGENT_FOLDER_COLORS[0]);
  const [editingFolder, setEditingFolder] = useState(null);
  const [moveMenuFor, setMoveMenuFor] = useState(null);
  const [showMenuFor, setShowMenuFor] = useState(null);
  // Drag-and-drop — agent → folder chip
  const [draggingAgentId, setDraggingAgentId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null); // folder_id | 'all' | null

  // Wizard state
  const TOTAL_STEPS = 5;
  const [wizardStep, setWizardStep] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    type: 'voice',
    direction: 'both',
    description: '',
    language: 'en',
    voice_engine: 'elevenlabs',
    voice_id: 'jessica',
    grok_voice: 'Eve',
    system_prompt: '',
    first_message: '',
    outbound_greeting: '',
    personality_traits: '',
    style_guardrails: DEFAULT_STYLE_GUARDRAILS,
    response_guidelines: DEFAULT_RESPONSE_GUIDELINES,
    functions: ['end_call', 'check_availability', 'book_appointment', 'schedule_callback'],
    knowledge_base_content: '',
    business_name: '',
    business_type: '',
    tone: '',
    chat_enabled: false,
    voice_enabled: true,
    ghl_calendars: [],
  });

  // Theme classes
  const cardBg = isDarkMode ? 'bg-[#0c0d12]/80' : 'bg-white/80';
  const cardBorder = isDarkMode ? 'border-white/[0.08]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const bgMain = isDarkMode ? 'bg-[#08090d]' : 'bg-gray-50';
  const inputBg = isDarkMode ? 'bg-white/5' : 'bg-gray-50';

  // LeadConnector calendar state
  const [ghlCalendars, setGhlCalendars] = useState([]);
  const [loadingGhlCalendars, setLoadingGhlCalendars] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);

  useEffect(() => { fetchAgents(); fetchFolders(); }, [subAccountId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenuFor && !event.target.closest('[data-menu-button]') && !event.target.closest('[data-menu-dropdown]')) {
        setShowMenuFor(null);
        setMoveMenuFor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenuFor]);

  const fetchAgents = async () => {
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/agents`);
      setAgents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await api.get(`/api/sub-accounts/${subAccountId}/agent-folders`);
      setFolders(res.data?.folders || []);
    } catch { /* ignore */ }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Folder name required'); return; }
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/agent-folders`, { name, color: newFolderColor });
      setFolders((f) => [...f, res.data]);
      setActiveFolderId(res.data.folder_id);
      setShowNewFolder(false);
      setNewFolderName('');
      setNewFolderColor(AGENT_FOLDER_COLORS[0]);
      toast.success('Folder created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create folder'); }
  };

  const saveFolderEdit = async () => {
    if (!editingFolder) return;
    try {
      await api.patch(`/api/sub-accounts/${subAccountId}/agent-folders/${editingFolder.folder_id}`, {
        name: editingFolder.name, color: editingFolder.color,
      });
      setFolders((list) => list.map(f => f.folder_id === editingFolder.folder_id ? { ...f, ...editingFolder } : f));
      setEditingFolder(null);
      toast.success('Folder updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Agents inside will move back to Unfiled.')) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/agent-folders/${folderId}`);
      setFolders((l) => l.filter(f => f.folder_id !== folderId));
      setEditingFolder(null);
      if (activeFolderId === folderId) setActiveFolderId('all');
      setAgents((l) => l.map(a => a.folder_id === folderId ? { ...a, folder_id: null } : a));
      toast.success('Folder deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const moveAgentToFolder = async (agentId, folderId) => {
    try {
      await api.patch(`/api/agents/${agentId}/folder`, { folder_id: folderId });
      setAgents((l) => l.map(a => a.agent_id === agentId ? { ...a, folder_id: folderId } : a));
      const fname = folderId ? (folders.find(f => f.folder_id === folderId)?.name || 'folder') : 'Unfiled';
      toast.success(`Moved to ${fname}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to move'); }
    setMoveMenuFor(null);
    setShowMenuFor(null);
  };

  const fetchGhlCalendars = async () => {
    setLoadingGhlCalendars(true);
    try {
      const response = await api.get(`/api/sub-accounts/${subAccountId}/ghl/calendars`);
      setGhlCalendars(response.data.calendars || []);
      setGhlConnected(true);
    } catch (error) {
      console.error('Failed to fetch LeadConnector calendars:', error);
      setGhlConnected(false);
    } finally {
      setLoadingGhlCalendars(false);
    }
  };

  // Fetch LeadConnector calendars when wizard opens
  useEffect(() => {
    if (showWizard) { fetchGhlCalendars(); }
  }, [showWizard]);

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) { toast.error('Please enter a website URL'); return; }
    setIsAnalyzing(true);
    try {
      const response = await api.post('/api/agents/scrape-website', {
        url: websiteUrl,
        agent_type: agentConfig.direction
      });

      if (response.data.ai_analysis) {
        setAnalysisResult(response.data);
        const a = response.data.ai_analysis;
        
        // Build single KB content from AI analysis
        let kbContent = '';
        if (typeof a.knowledge_base === 'string') {
          kbContent = a.knowledge_base;
        } else if (Array.isArray(a.knowledge_base)) {
          // Fallback: combine array entries into one document
          kbContent = a.knowledge_base.map(kb => {
            const title = kb.topic || '';
            const body = kb.content || '';
            return `### ${title}\n${body}`;
          }).join('\n\n');
        }

        setAgentConfig(prev => ({
          ...prev,
          name: a.agent_name || prev.name,
          description: `${a.business_type || ''} AI Assistant`,
          system_prompt: a.system_prompt || prev.system_prompt,
          first_message: a.first_message || prev.first_message,
          outbound_greeting: a.outbound_greeting || prev.outbound_greeting,
          functions: a.suggested_functions || prev.functions,
          business_name: a.business_name || '',
          business_type: a.business_type || '',
          tone: a.tone || '',
          knowledge_base_content: kbContent || prev.knowledge_base_content,
        }));

        toast.success('Website analyzed successfully!');
        setWizardStep(2);
      } else {
        toast.error(response.data.error || 'Analysis failed. Configure manually.');
        setWizardStep(2);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze website. You can configure manually.');
      setWizardStep(2);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!agentConfig.name.trim()) { toast.error('Please enter an agent name'); return; }
    setIsCreating(true);
    try {
      // Determine type — if both enabled, save as voice with chat_enabled
      const agentType = agentConfig.voice_enabled ? 'voice' : 'chat';
      const payload = {
        ...agentConfig,
        type: agentType,
        chat_enabled: agentConfig.chat_enabled,
        ghl_calendars: agentConfig.ghl_calendars,
        // Send single KB entry if content exists
        knowledge_base: agentConfig.knowledge_base_content.trim() ? [{
          topic: `${agentConfig.business_name || agentConfig.name || 'Business'} Knowledge Base`,
          content: agentConfig.knowledge_base_content.trim(),
          category: 'comprehensive'
        }] : [],
      };
      // Remove frontend-only fields
      delete payload.voice_enabled;
      delete payload.knowledge_base_content;

      await api.post(`/api/sub-accounts/${subAccountId}/agents/wizard`, payload);
      toast.success('Agent created successfully!');
      setShowWizard(false);
      resetWizard();
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateBlank = async () => {
    setCreatingBlank(true);
    try {
      const res = await api.post(`/api/sub-accounts/${subAccountId}/agents/blank`);
      const newId = res.data?.agent_id;
      if (!newId) throw new Error('No agent id returned');
      toast.success('Blank assistant created — configure it now');
      setShowCreateChooser(false);
      navigate(`/workspace/${workspaceId}/sub-account/${subAccountId}/agents/${newId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create blank assistant');
    } finally {
      setCreatingBlank(false);
    }
  };

  const resetWizard = () => {
    setWizardStep(1);
    setWebsiteUrl('');
    setAnalysisResult(null);
    setIsCreating(false);
    setAgentConfig({
      name: '', type: 'voice', direction: 'both', description: '', language: 'en',
      voice_engine: 'elevenlabs', voice_id: 'jessica', grok_voice: 'Eve',
      system_prompt: '', first_message: '', outbound_greeting: '',
      personality_traits: '',
      style_guardrails: DEFAULT_STYLE_GUARDRAILS,
      response_guidelines: DEFAULT_RESPONSE_GUIDELINES,
      functions: ['end_call', 'check_availability', 'book_appointment', 'schedule_callback'],
      knowledge_base_content: '', business_name: '', business_type: '', tone: '',
      chat_enabled: false, voice_enabled: true,
    });
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}/agents/${agentId}`);
      toast.success('Agent deleted');
      fetchAgents();
    } catch (error) { toast.error('Failed to delete agent'); }
    setShowMenuFor(null);
  };

  const handleToggleAgent = async (agentId, currentStatus) => {
    try {
      await api.put(`/api/sub-accounts/${subAccountId}/agents/${agentId}`, {
        status: currentStatus === 'active' ? 'paused' : 'active'
      });
      toast.success(`Agent ${currentStatus === 'active' ? 'paused' : 'activated'}`);
      fetchAgents();
    } catch (error) { toast.error('Failed to update agent'); }
    setShowMenuFor(null);
  };

  // Duplicate clones every configurable field on the source agent into a new
  // draft — new agent_id, new activation_tag, cleared EL bindings & counters,
  // " (Copy)" suffix. Lives on the backend so the copy is atomic and the EL
  // sync only fires on the user's first explicit save of the duplicate.
  const handleDuplicateAgent = async (agentId) => {
    setShowMenuFor(null);
    try {
      const resp = await api.post(`/api/sub-accounts/${subAccountId}/agents/${agentId}/duplicate`);
      toast.success(`Duplicated as "${resp.data?.name || 'copy'}"`);
      fetchAgents();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to duplicate agent');
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesFolder = true;
    // "All" chip acts as Unfiled — only shows agents not in any folder.
    // Folder chips show only agents in that folder.
    if (activeFolderId === 'all') matchesFolder = !agent.folder_id;
    else matchesFolder = agent.folder_id === activeFolderId;
    return matchesSearch && matchesFolder;
  });

  const toggleFunction = (funcId) => {
    setAgentConfig(prev => ({
      ...prev,
      functions: prev.functions.includes(funcId)
        ? prev.functions.filter(f => f !== funcId)
        : [...prev.functions, funcId]
    }));
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-screen ${bgMain}`}>
        <div className="w-12 h-12 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} p-6 lg:p-8`} data-testid="ai-agents-page">
      {/* Ambient Background */}
      {isDarkMode && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-violet-400/5 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-teal-400/5 rounded-full blur-[80px]" />
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className={`text-2xl lg:text-3xl font-display font-semibold ${textPrimary} mb-1`}>
                AI Agents
              </h1>
              <p className={textSecondary}>Create and manage intelligent voice & chat assistants</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowNewFolder(true)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${inputBg} border ${cardBorder} ${textPrimary} hover:bg-white/10`}
                data-testid="new-folder-btn"
                title="Create a folder to organize agents"
              >
                <FolderPlus size={16} />
                <span className="hidden sm:inline">New Folder</span>
              </button>
              <button
                onClick={() => setShowCreateChooser(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all"
                data-testid="create-agent-btn"
              >
                <Wand2 size={18} />
                Create AI Agent
              </button>
            </div>
          </div>
        </motion.div>

        {/* Folder chip row — premium glow chips */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="mb-4"
          data-testid="agent-folders-row"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setActiveFolderId('all')}
              onDragOver={(e) => { if (draggingAgentId) { e.preventDefault(); setDragOverFolder('all'); } }}
              onDragLeave={() => setDragOverFolder((v) => (v === 'all' ? null : v))}
              onDrop={(e) => {
                e.preventDefault();
                const aid = e.dataTransfer.getData('text/agent-id') || draggingAgentId;
                if (aid) moveAgentToFolder(aid, null);
                setDragOverFolder(null); setDraggingAgentId(null);
              }}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                dragOverFolder === 'all'
                  ? 'bg-teal-400/15 border-teal-400/60 text-teal-300 scale-105 shadow-[0_0_18px_-3px_rgba(45,212,191,0.6)]'
                  : activeFolderId === 'all'
                    ? `${inputBg} border-white/20 ${textPrimary}`
                    : `bg-white/[0.02] border-white/[0.06] ${textSecondary} hover:bg-white/[0.05]`
              }`}
              data-testid="agent-folder-chip-all"
            >
              All <span className="ml-2 text-[11px] opacity-70">{agents.filter(a => !a.folder_id).length}</span>
            </button>
            {folders.map((f) => {
              const count = agents.filter(a => a.folder_id === f.folder_id).length;
              const active = activeFolderId === f.folder_id;
              return (
                <div key={f.folder_id} className="relative group">
                  <button
                    onClick={() => setActiveFolderId(f.folder_id)}
                    onDoubleClick={() => setEditingFolder({ ...f })}
                    onDragOver={(e) => { if (draggingAgentId) { e.preventDefault(); setDragOverFolder(f.folder_id); } }}
                    onDragLeave={() => setDragOverFolder((v) => (v === f.folder_id ? null : v))}
                    onDrop={(e) => {
                      e.preventDefault();
                      const aid = e.dataTransfer.getData('text/agent-id') || draggingAgentId;
                      if (aid) moveAgentToFolder(aid, f.folder_id);
                      setDragOverFolder(null); setDraggingAgentId(null);
                    }}
                    className="relative px-4 py-2 rounded-xl text-sm font-semibold transition-all border flex items-center gap-2"
                    style={{
                      background: dragOverFolder === f.folder_id ? `${f.color}55` : active ? `${f.color}22` : `${f.color}10`,
                      borderColor: dragOverFolder === f.folder_id ? f.color : active ? `${f.color}66` : `${f.color}28`,
                      color: dragOverFolder === f.folder_id ? '#fff' : active ? '#fff' : f.color,
                      boxShadow: dragOverFolder === f.folder_id
                        ? `0 0 24px -2px ${f.color}, inset 0 0 0 1px ${f.color}`
                        : active
                          ? `0 0 18px -3px ${f.color}80, inset 0 0 0 1px ${f.color}33`
                          : `0 0 10px -4px ${f.color}66`,
                      transform: dragOverFolder === f.folder_id ? 'scale(1.06)' : 'scale(1)',
                    }}
                    data-testid={`agent-folder-chip-${f.folder_id}`}
                  >
                    <Folder size={13} style={{ color: f.color }} />
                    <span>{f.name}</span>
                    <span className="text-[11px] opacity-70">{count}</span>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingFolder({ ...f }); }}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0c0d12] border border-white/10 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`agent-folder-edit-${f.folder_id}`}
                    title="Edit folder"
                  >
                    <Pencil size={9} />
                  </button>
                </div>
              );
            })}
            {showNewFolder && (
              <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/15 rounded-xl pl-2 pr-1 py-1">
                <input
                  autoFocus value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
                  placeholder="Folder name" className="bg-transparent text-xs text-white placeholder-gray-500 focus:outline-none w-28"
                  data-testid="new-agent-folder-input"
                />
                <div className="flex items-center gap-0.5">
                  {AGENT_FOLDER_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setNewFolderColor(c)}
                      className={`w-3.5 h-3.5 rounded-full border ${newFolderColor === c ? 'border-white scale-110' : 'border-transparent'} transition-transform`}
                      style={{ background: c, boxShadow: newFolderColor === c ? `0 0 8px ${c}` : 'none' }}
                    />
                  ))}
                </div>
                <button onClick={createFolder} className="p-1 rounded-lg bg-teal-400 text-space-900 hover:bg-teal-500" data-testid="new-agent-folder-confirm"><Check size={12} /></button>
                <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="p-1 rounded-lg text-gray-400 hover:bg-white/10"><X size={12} /></button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-4 mb-6`}
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${textSecondary}`} />
              <input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-9 pr-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
              />
            </div>
          </div>
        </motion.div>

        {/* Agents List — clean rows (click row to configure) */}
        {filteredAgents.length > 0 ? (
          <div className="space-y-2">
            {/* Column labels */}
            <div className={`hidden lg:flex items-center gap-4 px-4 pt-1 pb-0.5 text-[10px] uppercase tracking-wider font-semibold ${textSecondary}`} data-testid="agent-list-header">
              <span className="flex-1 min-w-0">Agent</span>
              <span className="w-[240px] shrink-0">Chat — Tag · Model</span>
              <span className="w-[110px] shrink-0">Voice Model</span>
              <span className="w-[110px] shrink-0">Calendar</span>
              <span className="w-[92px] shrink-0">Created</span>
              <span className="w-[92px] shrink-0">Updated</span>
              <span className="w-[150px] shrink-0">Agent ID</span>
              <span className="w-9 shrink-0" />
            </div>
            {filteredAgents.map((agent, index) => {
              const chatOn = !!agent.chat_enabled;
              const calConnected = Array.isArray(agent.ghl_calendars) && agent.ghl_calendars.length > 0;
              return (
              <motion.div
                key={agent.agent_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.3) }}
                draggable
                onDragStart={(e) => {
                  setDraggingAgentId(agent.agent_id);
                  e.dataTransfer.setData('text/agent-id', agent.agent_id);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => { setDraggingAgentId(null); setDragOverFolder(null); }}
                onClick={() => navigate(`/workspace/${workspaceId}/sub-account/${subAccountId}/agents/${agent.agent_id}`)}
                data-testid={`agent-row-${agent.agent_id}`}
                className={`${cardBg} backdrop-blur-xl rounded-xl border ${cardBorder} px-4 py-3 relative group hover:border-teal-400/30 transition-all cursor-pointer ${showMenuFor === agent.agent_id ? 'z-30' : ''} ${draggingAgentId === agent.agent_id ? 'opacity-50 scale-[0.99]' : ''}`}
              >
                <div className="flex items-center gap-x-4 gap-y-2 flex-wrap lg:flex-nowrap">
                  {/* Icon + name */}
                  <div className="flex items-center gap-3 min-w-0 w-full lg:w-auto lg:flex-1">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      agent.type === 'voice'
                        ? 'bg-gradient-to-br from-violet-400 to-violet-600'
                        : 'bg-gradient-to-br from-teal-400 to-teal-600'
                    }`}>
                      {agent.type === 'voice' ? <Mic className="text-white" size={15} /> : <MessageSquare className="text-white" size={15} />}
                    </div>
                    <div className="min-w-0 flex items-center gap-2">
                      <h3 className={`text-sm font-semibold ${textPrimary} truncate`}>{agent.name}</h3>
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${agent.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`}
                        title={agent.status === 'active' ? 'Active' : 'Paused'}
                      />
                    </div>
                  </div>

                  {/* Chat: activation tag + chat model, or disabled */}
                  <div className="flex items-center gap-1.5 min-w-0 lg:w-[240px] lg:shrink-0" data-testid={`agent-chat-info-${agent.agent_id}`}>
                    <MessageSquare size={11} className={`shrink-0 ${chatOn ? 'text-teal-400' : 'opacity-40'}`} />
                    {chatOn ? (
                      <>
                        {agent.activation_tag && (
                          <span
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-400/10 text-teal-400 truncate max-w-[130px]"
                            title={`Chat activation tag: ${agent.activation_tag}`}
                          >
                            {agent.activation_tag}
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${inputBg} ${textSecondary} whitespace-nowrap`} title="Chat AI model">
                          {agent.chat_model ? modelLabel(agent.chat_model) : modelLabel(agent.ai_model)}
                        </span>
                      </>
                    ) : (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${inputBg} ${textSecondary} opacity-60`}>Chat disabled</span>
                    )}
                  </div>

                  {/* Voice AI model */}
                  <div className="flex items-center gap-1.5 lg:w-[130px] lg:shrink-0" data-testid={`agent-voice-model-${agent.agent_id}`}>
                    <Mic size={11} className="text-violet-400 shrink-0" />
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${inputBg} ${textSecondary} whitespace-nowrap`} title="Voice AI model">
                      {modelLabel(agent.ai_model)}
                    </span>
                  </div>

                  {/* Calendar */}
                  <div className="flex items-center gap-1.5 lg:w-[110px] lg:shrink-0" data-testid={`agent-calendar-${agent.agent_id}`}>
                    <Calendar size={11} className={`shrink-0 ${calConnected ? 'text-green-400' : 'opacity-40'}`} />
                    <span className={`text-[10px] font-medium ${calConnected ? 'text-green-400' : `${textSecondary} opacity-60`}`}>
                      {calConnected ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>

                  {/* Created / Updated */}
                  <div className={`flex items-center gap-1.5 lg:w-[92px] lg:shrink-0 text-[11px] ${textSecondary}`} data-testid={`agent-created-${agent.agent_id}`}>
                    <span className="lg:hidden text-[9px] uppercase tracking-wider opacity-60">Created</span>
                    {fmtAgentDate(agent.created_at)}
                  </div>
                  <div className={`flex items-center gap-1.5 lg:w-[92px] lg:shrink-0 text-[11px] ${textSecondary}`} data-testid={`agent-updated-${agent.agent_id}`}>
                    <span className="lg:hidden text-[9px] uppercase tracking-wider opacity-60">Updated</span>
                    {agent.updated_at ? fmtAgentDate(agent.updated_at) : '—'}
                  </div>

                  {/* Partial Agent ID — copy */}
                  <div className="lg:w-[150px] lg:shrink-0">
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(agent.agent_id);
                      } catch {
                        const ta = document.createElement('textarea');
                        ta.value = agent.agent_id;
                        ta.style.position = 'fixed';
                        ta.style.opacity = '0';
                        document.body.appendChild(ta);
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                      }
                      toast.success('Agent ID copied!');
                    }}
                    className={`flex items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-md shrink-0 ${isDarkMode ? 'bg-white/[0.04] hover:bg-teal-400/10' : 'bg-gray-50 hover:bg-teal-50'} ${textSecondary} hover:text-teal-400 border ${isDarkMode ? 'border-white/[0.06] hover:border-teal-400/30' : 'border-gray-200 hover:border-teal-300'} transition-all`}
                    title={`Click to copy full ID: ${agent.agent_id}`}
                    data-testid={`copy-agent-id-${agent.agent_id}`}
                  >
                    <span>{`${agent.agent_id.slice(0, 11)}…${agent.agent_id.slice(-4)}`}</span>
                    <Copy size={10} className="opacity-40 group-hover:opacity-100 transition-opacity" />
                  </button>
                  </div>

                  {/* 3-dots menu */}
                  <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-menu-button
                      onClick={() => setShowMenuFor(showMenuFor === agent.agent_id ? null : agent.agent_id)}
                      className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10 transition-colors`}
                      data-testid={`agent-menu-btn-${agent.agent_id}`}
                    >
                      <MoreVertical size={16} />
                    </button>

                    {showMenuFor === agent.agent_id && (
                      <div
                        data-menu-dropdown
                        className={`absolute right-0 top-full mt-1 w-44 ${isDarkMode ? 'bg-space-800' : 'bg-white'} border ${cardBorder} rounded-xl shadow-xl z-50`}
                      >
                        <div className="p-1">
                          <button
                            onClick={() => { handleToggleAgent(agent.agent_id, agent.status); setShowMenuFor(null); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg transition-colors`}
                          >
                            {agent.status === 'active' ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Activate</>}
                          </button>
                          <button
                            onClick={() => handleDuplicateAgent(agent.agent_id)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg transition-colors`}
                            data-testid={`action-duplicate-${agent.agent_id}`}
                          >
                            <Copy size={14} /> Duplicate
                          </button>
                          <button
                            onClick={() => setMoveMenuFor(moveMenuFor === agent.agent_id ? null : agent.agent_id)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg transition-colors`}
                            data-testid={`action-move-folder-${agent.agent_id}`}
                          >
                            <span className="flex items-center gap-2"><FolderOpen size={14} /> Move to folder</span>
                            <ChevronRight size={12} className={textSecondary} />
                          </button>
                          {moveMenuFor === agent.agent_id && (
                            <div className={`mx-1 my-1 p-1 rounded-xl ${inputBg} border ${cardBorder}`} data-testid={`move-folder-submenu-${agent.agent_id}`}>
                              <button
                                onClick={() => moveAgentToFolder(agent.agent_id, null)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textSecondary} hover:bg-white/10 rounded-lg`}
                                data-testid={`move-agent-unfiled-${agent.agent_id}`}
                              >
                                <Folder size={13} /> Unfiled
                              </button>
                              {folders.length === 0 && (
                                <p className={`px-3 py-2 text-[11px] ${textSecondary}`}>No folders yet — create one with "New Folder".</p>
                              )}
                              {folders.map((f) => (
                                <button
                                  key={f.folder_id}
                                  onClick={() => moveAgentToFolder(agent.agent_id, f.folder_id)}
                                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${textPrimary} hover:bg-white/10 rounded-lg`}
                                  data-testid={`move-agent-to-${f.folder_id}`}
                                >
                                  <Folder size={13} style={{ color: f.color }} />
                                  {f.name}
                                  {agent.folder_id === f.folder_id && <Check size={11} className="ml-auto text-green-400" />}
                                </button>
                              ))}
                            </div>
                          )}
                          <hr className={`my-1 ${cardBorder}`} />
                          <button
                            onClick={() => { handleDeleteAgent(agent.agent_id); setShowMenuFor(null); }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );})}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${cardBg} backdrop-blur-xl rounded-2xl border ${cardBorder} p-16 text-center`}
          >
            <div className="relative inline-block mb-6">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center">
                <Bot className={isDarkMode ? 'text-gray-600' : 'text-gray-400'} size={48} />
              </div>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center">
                <Sparkles className="text-white" size={20} />
              </div>
            </div>
            <h3 className={`text-xl font-semibold ${textPrimary} mb-2`}>No AI Agents Yet</h3>
            <p className={`${textSecondary} mb-8 max-w-md mx-auto`}>
              Create your first AI agent using our intelligent wizard. Just provide your website URL and we'll do the rest.
            </p>
            <button
              onClick={() => setShowWizard(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white font-medium hover:shadow-lg hover:shadow-teal-400/25 transition-all"
            >
              <Wand2 size={18} />
              Create Your First Agent
            </button>
          </motion.div>
        )}
      </div>

      {/* Create AI Agent — Chooser Modal */}
      <AnimatePresence>
        {showCreateChooser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => !creatingBlank && setShowCreateChooser(false)}
            data-testid="create-agent-chooser-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-3xl ${isDarkMode ? 'bg-[#0a0b10]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl`}
              onClick={(e) => e.stopPropagation()}
              data-testid="create-agent-chooser"
            >
              <div className={`px-6 pt-6 pb-2 flex items-start justify-between ${textPrimary}`}>
                <div>
                  <h2 className="text-xl font-display font-semibold">Create AI Agent</h2>
                  <p className={`text-sm ${textSecondary} mt-1`}>Pick how you want to start</p>
                </div>
                <button
                  onClick={() => !creatingBlank && setShowCreateChooser(false)}
                  className={`p-1.5 rounded-lg ${textSecondary} hover:bg-white/5 transition-colors`}
                  data-testid="chooser-close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Existing generated-agent workflow, now in Project Manager. */}
                <button
                  type="button"
                  onClick={() => { setShowCreateChooser(false); navigate(`/workspace/${workspaceId}/sub-account/${subAccountId}/project-manager`); }}
                  className="group relative text-left rounded-2xl p-6 overflow-hidden transition-all hover:-translate-y-0.5 focus:outline-none"
                  style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.18) 0%, rgba(139,92,246,0.18) 100%)', border: '1px solid rgba(20,184,166,0.35)' }}
                  data-testid="chooser-option-wizard"
                >
                  <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none opacity-40 blur-3xl transition-opacity group-hover:opacity-60"
                       style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.5), transparent 70%)' }} />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center shadow-[0_0_24px_-4px_rgba(20,184,166,0.6)]">
                      <Wand2 size={22} className="text-white" />
                    </div>
                    <h3 className={`mt-4 text-base font-semibold ${textPrimary}`}>Create with AI Architect</h3>
                    <p className={`mt-1 text-sm ${textSecondary} leading-relaxed`}>
                      Choose a brand profile, then AI Architect builds the agent,
                      prompts, knowledge base &amp; widget for you.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-teal-300">
                      Open AI Architect <ArrowRight size={14} />
                    </div>
                  </div>
                </button>

                {/* Option 2 — Blank Canvas */}
                <button
                  type="button"
                  disabled={creatingBlank}
                  onClick={handleCreateBlank}
                  className="group relative text-left rounded-2xl p-6 overflow-hidden transition-all hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-wait focus:outline-none"
                  style={{ background: 'linear-gradient(135deg, rgba(148,163,184,0.10) 0%, rgba(59,130,246,0.15) 100%)', border: '1px solid rgba(148,163,184,0.25)' }}
                  data-testid="chooser-option-blank"
                >
                  <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full pointer-events-none opacity-30 blur-3xl transition-opacity group-hover:opacity-50"
                       style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.45), transparent 70%)' }} />
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-500 to-blue-500 flex items-center justify-center shadow-[0_0_24px_-4px_rgba(59,130,246,0.55)]">
                      {creatingBlank ? <Loader2 size={22} className="text-white animate-spin" /> : <PenTool size={22} className="text-white" />}
                    </div>
                    <h3 className={`mt-4 text-base font-semibold ${textPrimary}`}>Create Assistant</h3>
                    <p className={`mt-1 text-sm ${textSecondary} leading-relaxed`}>
                      Start from a blank canvas. You'll land right in the editor to craft the prompt,
                      voice, and functions yourself.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-blue-300">
                      {creatingBlank ? 'Creating…' : <>Open editor <ArrowRight size={14} /></>}
                    </div>
                  </div>
                </button>
              </div>

              <div className={`px-6 pb-5 pt-1 text-[11px] ${textSecondary}`}>
                New blank assistants start in <b className="text-amber-300">draft</b> status — activate them once configured.
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Agent Creation Wizard Modal */}
      <AnimatePresence>
        {showWizard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowWizard(false); resetWizard(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`w-full max-w-2xl ${isDarkMode ? 'bg-[#0a0b10]' : 'bg-white'} rounded-2xl overflow-hidden border ${cardBorder} shadow-2xl max-h-[90vh] flex flex-col`}
              onClick={e => e.stopPropagation()}
            >
              {/* Wizard Header — sticky */}
              <div className={`p-5 border-b ${cardBorder} ${isDarkMode ? 'bg-[#0a0b10]' : 'bg-white'} shrink-0`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center">
                      <Wand2 className="text-white" size={18} />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold ${textPrimary}`}>AI Agent Wizard</h3>
                      <p className={`text-xs ${textSecondary}`}>Step {wizardStep} of {TOTAL_STEPS}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => { setShowWizard(false); resetWizard(); }}
                    className={`p-2 rounded-lg ${textSecondary} hover:bg-white/10 transition-colors`}
                    data-testid="wizard-close-btn"
                  >
                    <X size={18} />
                  </button>
                </div>
                {/* Progress Bar */}
                <div className="mt-3 flex gap-1.5">
                  {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(step => (
                    <div 
                      key={step}
                      className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                        step <= wizardStep ? 'bg-gradient-to-r from-teal-400 to-violet-500' : isDarkMode ? 'bg-white/[0.06]' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto">

              {/* ═══════ Step 1: Website & Direction ═══════ */}
              {wizardStep === 1 && (
                <div className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400/10 to-violet-400/10 flex items-center justify-center mx-auto mb-3">
                      <Globe className="text-teal-400" size={28} />
                    </div>
                    <h4 className={`text-lg font-semibold ${textPrimary} mb-1`}>Enter Your Website</h4>
                    <p className={`text-sm ${textSecondary} max-w-md mx-auto`}>
                      We'll analyze your website and auto-generate a knowledge base, prompt, and agent config
                    </p>
                  </div>

                  <div className="space-y-5 max-w-lg mx-auto">
                    {/* URL */}
                    <div>
                      <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Website URL</label>
                      <div className="relative">
                        <Globe size={16} className={`absolute left-3.5 top-1/2 -translate-y-1/2 ${textSecondary}`} />
                        <input
                          type="url"
                          value={websiteUrl}
                          onChange={(e) => setWebsiteUrl(e.target.value)}
                          placeholder="https://your-business.com"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50 transition-colors`}
                          data-testid="website-url-input"
                        />
                      </div>
                    </div>

                    {/* Direction */}
                    <div>
                      <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Call Direction</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: 'inbound', label: 'Inbound', desc: 'Answer calls', icon: PhoneIncoming, color: 'teal' },
                          { id: 'outbound', label: 'Outbound', desc: 'Make calls', icon: PhoneOutgoing, color: 'violet' },
                          { id: 'both', label: 'Both', desc: 'Full coverage', icon: Phone, color: 'emerald' },
                        ].map(d => {
                          const Icon = d.icon;
                          const sel = agentConfig.direction === d.id;
                          return (
                            <button key={d.id} type="button" data-testid={`direction-${d.id}`}
                              onClick={() => setAgentConfig(p => ({ ...p, direction: d.id }))}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                sel ? `border-${d.color}-400/50 bg-${d.color}-400/10` : `${cardBorder} ${inputBg} hover:border-white/20`
                              }`}
                            >
                              <Icon className={`mb-1 ${sel ? `text-${d.color}-400` : textSecondary}`} size={18} />
                              <p className={`text-sm font-medium ${sel ? `text-${d.color}-400` : textPrimary}`}>{d.label}</p>
                              <p className={`text-[10px] ${textSecondary}`}>{d.desc}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Agent Capabilities */}
                    <div>
                      <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Agent Capabilities</label>
                      <div className="grid grid-cols-2 gap-2">
                        {/* Voice Toggle */}
                        <button type="button" data-testid="toggle-voice"
                          onClick={() => setAgentConfig(p => ({ ...p, voice_enabled: !p.voice_enabled }))}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            agentConfig.voice_enabled 
                              ? 'border-violet-400/50 bg-violet-400/10' 
                              : `${cardBorder} ${inputBg} hover:border-white/20`
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Mic className={agentConfig.voice_enabled ? 'text-violet-400' : textSecondary} size={18} />
                            <div className={`w-8 h-4.5 rounded-full relative transition-colors ${agentConfig.voice_enabled ? 'bg-violet-400' : isDarkMode ? 'bg-white/10' : 'bg-gray-300'}`}>
                              <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${agentConfig.voice_enabled ? 'left-[calc(100%-16px)]' : 'left-0.5'}`} />
                            </div>
                          </div>
                          <p className={`text-sm font-medium ${agentConfig.voice_enabled ? 'text-violet-400' : textPrimary}`}>Voice Agent</p>
                          <p className={`text-[10px] ${textSecondary}`}>Phone calls & conversations</p>
                        </button>
                        {/* Chat Toggle */}
                        <button type="button" data-testid="toggle-chat"
                          onClick={() => setAgentConfig(p => ({ ...p, chat_enabled: !p.chat_enabled }))}
                          className={`p-3 rounded-xl border text-left transition-all ${
                            agentConfig.chat_enabled 
                              ? 'border-teal-400/50 bg-teal-400/10' 
                              : `${cardBorder} ${inputBg} hover:border-white/20`
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <MessageSquare className={agentConfig.chat_enabled ? 'text-teal-400' : textSecondary} size={18} />
                            <div className={`w-8 h-4.5 rounded-full relative transition-colors ${agentConfig.chat_enabled ? 'bg-teal-400' : isDarkMode ? 'bg-white/10' : 'bg-gray-300'}`}>
                              <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all ${agentConfig.chat_enabled ? 'left-[calc(100%-16px)]' : 'left-0.5'}`} />
                            </div>
                          </div>
                          <p className={`text-sm font-medium ${agentConfig.chat_enabled ? 'text-teal-400' : textPrimary}`}>Chat Agent</p>
                          <p className={`text-[10px] ${textSecondary}`}>Web widget & messaging</p>
                        </button>
                      </div>
                      {!agentConfig.voice_enabled && !agentConfig.chat_enabled && (
                        <p className="text-xs text-amber-400 mt-2 flex items-center gap-1"><Info size={12} /> Enable at least one capability</p>
                      )}
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => setWizardStep(2)}
                        className={`flex-1 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10 transition-colors`}
                      >
                        Skip Analysis
                      </button>
                      <button
                        onClick={handleAnalyzeWebsite}
                        disabled={isAnalyzing || (!agentConfig.voice_enabled && !agentConfig.chat_enabled)}
                        className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-400/20 transition-all disabled:opacity-50"
                        data-testid="analyze-website-btn"
                      >
                        {isAnalyzing ? <><Loader2 size={16} className="animate-spin" /> Analyzing...</> : <><Sparkles size={16} /> Analyze & Generate</>}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══════ Step 2: Agent Identity & Voice ═══════ */}
              {wizardStep === 2 && (
                <div className="p-6">
                  <div className="mb-5">
                    <h4 className={`text-lg font-semibold ${textPrimary} mb-1`}>Agent Identity & Voice</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      {analysisResult ? 'Pre-filled from your website. Customize as needed.' : 'Configure your agent\'s identity and voice settings.'}
                    </p>
                  </div>

                  {analysisResult?.ai_analysis && (
                    <div className={`mb-5 p-3 rounded-xl ${isDarkMode ? 'bg-teal-400/5' : 'bg-teal-50'} border border-teal-400/20`}>
                      <div className="flex items-center gap-2 text-teal-400">
                        <Check size={14} />
                        <span className="text-xs font-medium">AI Analysis Complete</span>
                        <span className={`text-xs ${textSecondary} ml-1`}>
                          Detected: <span className={textPrimary}>{analysisResult.ai_analysis.business_name}</span>
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {/* Name + Language row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Agent Name</label>
                        <input type="text" value={agentConfig.name} data-testid="agent-name-input"
                          onChange={(e) => setAgentConfig(p => ({ ...p, name: e.target.value }))}
                          placeholder="e.g., Sarah, Alex, Maya"
                          className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                        />
                      </div>
                      <div>
                        <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Language</label>
                        <select value={agentConfig.language}
                          onChange={(e) => setAgentConfig(p => ({ ...p, language: e.target.value }))}
                          className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm focus:outline-none focus:border-teal-400/50`}
                        >
                          {LANGUAGE_OPTIONS.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.flag} {lang.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Voice Engine — only show if voice enabled */}
                    {agentConfig.voice_enabled && (
                      <>
                        <div>
                          <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Voice Engine</label>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" data-testid="wizard-engine-elevenlabs"
                              onClick={() => setAgentConfig(p => ({ ...p, voice_engine: 'elevenlabs' }))}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                agentConfig.voice_engine === 'elevenlabs'
                                  ? 'bg-teal-400/10 border-teal-400/30'
                                  : `${inputBg} ${cardBorder} hover:border-white/20`
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-0.5">
                                <Volume2 size={14} className={agentConfig.voice_engine === 'elevenlabs' ? 'text-teal-400' : textSecondary} />
                                <span className={`text-sm font-semibold ${agentConfig.voice_engine === 'elevenlabs' ? 'text-teal-400' : textPrimary}`}>ElevenLabs</span>
                                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full text-emerald-400 bg-emerald-400/10">STANDARD</span>
                              </div>
                              <p className={`text-[10px] ${textSecondary}`}>Premium TTS, 6+ voices, Flash v2.5</p>
                            </button>
                            <button type="button" data-testid="wizard-engine-grok"
                              onClick={() => setAgentConfig(p => ({ ...p, voice_engine: 'grok' }))}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                agentConfig.voice_engine === 'grok'
                                  ? 'bg-orange-400/10 border-orange-400/30'
                                  : `${inputBg} ${cardBorder} hover:border-white/20`
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-0.5">
                                <Zap size={14} className={agentConfig.voice_engine === 'grok' ? 'text-orange-400' : textSecondary} />
                                <span className={`text-sm font-semibold ${agentConfig.voice_engine === 'grok' ? 'text-orange-400' : textPrimary}`}>Grok by xAI</span>
                                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full text-orange-400 bg-orange-400/10">PREMIUM</span>
                              </div>
                              <p className={`text-[10px] ${textSecondary}`}>Native speech-to-speech, ultra-low latency</p>
                            </button>
                          </div>
                        </div>

                        {/* Voice Selection */}
                        {agentConfig.voice_engine === 'elevenlabs' ? (
                          <div>
                            <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Select Voice</label>
                            <div className="grid grid-cols-3 gap-2">
                              {VOICE_OPTIONS.map(voice => {
                                const sel = agentConfig.voice_id === voice.id;
                                return (
                                  <button key={voice.id} type="button" data-testid={`voice-${voice.id}`}
                                    onClick={() => setAgentConfig(p => ({ ...p, voice_id: voice.id }))}
                                    className={`p-2.5 rounded-xl border text-left transition-all ${
                                      sel ? 'border-violet-400/50 bg-violet-400/10' : `${cardBorder} ${inputBg} hover:border-white/20`
                                    }`}
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <Volume2 className={sel ? 'text-violet-400' : textSecondary} size={14} />
                                      {sel && <Check className="text-violet-400" size={12} />}
                                    </div>
                                    <p className={`text-sm font-medium ${sel ? 'text-violet-400' : textPrimary}`}>{voice.name}</p>
                                    <p className={`text-[10px] ${textSecondary}`}>{voice.gender} &middot; {voice.accent}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>Grok Voice</label>
                            <div className="grid grid-cols-5 gap-2">
                              {GROK_VOICE_OPTIONS.map(v => {
                                const sel = agentConfig.grok_voice === v.id;
                                return (
                                  <button key={v.id} type="button" data-testid={`grok-voice-${v.id}`}
                                    onClick={() => setAgentConfig(p => ({ ...p, grok_voice: v.id }))}
                                    className={`p-2 rounded-xl border text-center transition-all ${
                                      sel ? 'border-orange-400/50 bg-orange-400/10' : `${cardBorder} ${inputBg} hover:border-white/20`
                                    }`}
                                  >
                                    <p className={`text-sm font-semibold ${sel ? 'text-orange-400' : textPrimary}`}>{v.id}</p>
                                    <p className={`text-[9px] ${textSecondary}`}>{v.gender}</p>
                                    <p className={`text-[9px] ${sel ? v.color : textSecondary}`}>{v.tone}</p>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Greeting Messages */}
                    {(agentConfig.direction === 'inbound' || agentConfig.direction === 'both') && (
                      <div>
                        <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>
                          {agentConfig.direction === 'both' ? 'Inbound Greeting' : 'Greeting Message'}
                        </label>
                        <input type="text" value={agentConfig.first_message} data-testid="wizard-greeting-input"
                          onChange={(e) => setAgentConfig(p => ({ ...p, first_message: e.target.value }))}
                          placeholder="Hey! Thanks for calling [Business]. This is [Name], how can I help?"
                          className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                        />
                      </div>
                    )}
                    {(agentConfig.direction === 'outbound' || agentConfig.direction === 'both') && (
                      <div>
                        <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>
                          {agentConfig.direction === 'both' ? 'Outbound Greeting' : 'Greeting Message'}
                          {agentConfig.direction === 'both' && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-400 font-bold normal-case">OUTBOUND</span>}
                        </label>
                        <input type="text" value={agentConfig.outbound_greeting} data-testid="wizard-outbound-greeting-input"
                          onChange={(e) => setAgentConfig(p => ({ ...p, outbound_greeting: e.target.value }))}
                          placeholder="Hey! This is [Name] from [Business]. How are you doing today?"
                          className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Nav */}
                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setWizardStep(1)} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10 transition-colors`}>
                      <ChevronLeft size={16} /> Back
                    </button>
                    <button onClick={() => setWizardStep(3)} data-testid="wizard-step2-next"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-400/20 transition-all">
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* ═══════ Step 3: Knowledge Base ═══════ */}
              {wizardStep === 3 && (
                <div className="p-6">
                  <div className="mb-5">
                    <h4 className={`text-lg font-semibold ${textPrimary} mb-1`}>Knowledge Base</h4>
                    <p className={`text-sm ${textSecondary}`}>
                      This is the agent's brain. The more detailed the information, the smarter and more valuable the AI will be during conversations. You can always edit this later in the Knowledge Base section.
                    </p>
                  </div>

                  {analysisResult?.ai_analysis && agentConfig.knowledge_base_content && (
                    <div className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-teal-400/5' : 'bg-teal-50'} border border-teal-400/20`}>
                      <div className="flex items-center gap-2 text-teal-400">
                        <Check size={14} />
                        <span className="text-xs font-medium">Auto-generated from website analysis</span>
                      </div>
                    </div>
                  )}

                  <div className={`rounded-xl border ${cardBorder} overflow-hidden`}>
                    <div className={`px-4 py-2.5 ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'} border-b ${cardBorder} flex items-center gap-2`}>
                      <Database size={14} className="text-teal-400" />
                      <span className={`text-xs font-medium ${textSecondary} uppercase tracking-wider`}>Business Knowledge</span>
                      <span className={`text-[10px] ${textSecondary} ml-auto`}>Include: company overview, services, pricing, FAQs, policies, hours</span>
                    </div>
                    <textarea
                      value={agentConfig.knowledge_base_content}
                      onChange={(e) => setAgentConfig(p => ({ ...p, knowledge_base_content: e.target.value }))}
                      rows={14}
                      className={`w-full px-4 py-3 ${inputBg} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none resize-none border-0`}
                      placeholder={`### Company Overview\nWe are [Business Name], a [industry] company based in [location]. We specialize in...\n\n### Services & Pricing\n- Service 1: Description, starting at $X\n- Service 2: Description, starting at $X\n- Service 3: Description, included with...\n\n### Frequently Asked Questions\nQ: What areas do you serve?\nA: We serve the greater [area] region including...\n\nQ: What are your hours?\nA: We're available Monday-Friday 9am-5pm...\n\nQ: Do you offer free estimates?\nA: Yes, we offer complimentary consultations...\n\n### Policies & Info\n- Free consultations available\n- Licensed and insured\n- Same-day appointments when available`}
                      data-testid="wizard-kb-content"
                    />
                  </div>

                  <div className={`mt-3 flex items-center gap-1.5 text-xs ${textSecondary}`}>
                    <Info size={12} className="text-teal-400/60" />
                    This will be saved to the Knowledge Base section and linked to this agent
                  </div>

                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setWizardStep(2)} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10 transition-colors`}>
                      <ChevronLeft size={16} /> Back
                    </button>
                    <button onClick={() => setWizardStep(4)} data-testid="wizard-step3-next"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-400/20 transition-all">
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* ═══════ Step 4: Functions & Calendar ═══════ */}
              {wizardStep === 4 && (
                <div className="p-6">
                  <div className="mb-5">
                    <h4 className={`text-lg font-semibold ${textPrimary} mb-1`}>Functions & Calendar</h4>
                    <p className={`text-sm ${textSecondary}`}>Toggle the functions your agent can use and select the LeadConnector calendar for booking. You can always adjust these later.</p>
                  </div>

                  {/* Functions */}
                  <div className="space-y-1.5 mb-6">
                    {FUNCTION_OPTIONS.map(func => {
                      const enabled = agentConfig.functions.includes(func.id);
                      return (
                        <button key={func.id} type="button" data-testid={`func-${func.id}`}
                          onClick={() => toggleFunction(func.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                            enabled
                              ? 'border-teal-400/30 bg-teal-400/5'
                              : `${cardBorder} ${inputBg} hover:border-white/15`
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            enabled ? 'bg-teal-400/15' : isDarkMode ? 'bg-white/5' : 'bg-gray-100'
                          }`}>
                            <Settings2 size={14} className={enabled ? 'text-teal-400' : textSecondary} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-mono ${enabled ? 'text-teal-400' : textPrimary}`}>{func.name}</p>
                            <p className={`text-[11px] ${textSecondary} truncate`}>{func.description}</p>
                          </div>
                          <div className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${enabled ? 'bg-teal-400' : isDarkMode ? 'bg-white/10' : 'bg-gray-300'}`}>
                            <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${enabled ? 'left-[calc(100%-18px)]' : 'left-0.5'}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* LeadConnector Calendar Selection */}
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'} border ${cardBorder}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={16} className="text-teal-400" />
                      <h5 className={`text-sm font-semibold ${textPrimary}`}>LeadConnector Calendar</h5>
                    </div>
                    <p className={`text-xs ${textSecondary} mb-3`}>
                      Select a calendar for the AI to use when booking appointments.
                    </p>

                    {!ghlConnected ? (
                      <div className={`p-4 rounded-lg ${inputBg} border ${cardBorder} text-center`}>
                        <Calendar size={24} className={`mx-auto ${textSecondary} mb-2`} />
                        <p className={`text-xs ${textSecondary} mb-2`}>LeadConnector not connected — you can connect later in Integrations.</p>
                      </div>
                    ) : loadingGhlCalendars ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={20} className="text-teal-400 animate-spin" />
                      </div>
                    ) : ghlCalendars.length > 0 ? (
                      <div className="space-y-1.5">
                        {ghlCalendars.map(calendar => {
                          const isSelected = agentConfig.ghl_calendars?.includes(calendar.id);
                          return (
                            <div key={calendar.id}
                              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected ? 'bg-teal-400/10 border-teal-400/30' : `${inputBg} ${cardBorder} hover:border-white/20`
                              }`}
                              onClick={() => {
                                const current = agentConfig.ghl_calendars || [];
                                const updated = isSelected ? current.filter(id => id !== calendar.id) : [...current, calendar.id];
                                setAgentConfig(p => ({ ...p, ghl_calendars: updated }));
                              }}
                              data-testid={`wizard-calendar-${calendar.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Calendar size={14} className={isSelected ? 'text-teal-400' : textSecondary} />
                                  <div>
                                    <p className={`text-sm font-medium ${isSelected ? 'text-teal-400' : textPrimary}`}>{calendar.name}</p>
                                    {calendar.description && <p className={`text-[10px] ${textSecondary}`}>{calendar.description}</p>}
                                  </div>
                                </div>
                                {isSelected && <Check size={16} className="text-teal-400" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className={`p-3 rounded-lg ${inputBg} border ${cardBorder} text-center`}>
                        <p className={`text-xs ${textSecondary}`}>No calendars found in LeadConnector. Create one in LeadConnector first.</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setWizardStep(3)} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10 transition-colors`}>
                      <ChevronLeft size={16} /> Back
                    </button>
                    <button onClick={() => setWizardStep(5)} data-testid="wizard-step4-next"
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-400/20 transition-all">
                      Continue <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* ═══════ Step 5: Prompt & Review ═══════ */}
              {wizardStep === 5 && (
                <div className="p-6">
                  <div className="mb-5">
                    <h4 className={`text-lg font-semibold ${textPrimary} mb-1`}>Review & Launch</h4>
                    <p className={`text-sm ${textSecondary}`}>Review the AI-generated prompt and your agent summary. Edit anything before creating.</p>
                  </div>

                  <div className="space-y-4">
                    {/* System Prompt */}
                    <div>
                      <label className={`block text-xs font-medium ${textSecondary} mb-1.5 uppercase tracking-wider`}>System Prompt (AI Instructions)</label>
                      <textarea value={agentConfig.system_prompt}
                        onChange={(e) => setAgentConfig(p => ({ ...p, system_prompt: e.target.value }))}
                        rows={8}
                        className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm font-mono placeholder-gray-500 focus:outline-none focus:border-teal-400/50 resize-none`}
                        placeholder="## Identity&#10;You are [Name], an AI assistant for [Business]...&#10;&#10;## Tasks&#10;### Greeting&#10;..."
                        data-testid="wizard-system-prompt"
                      />
                    </div>

                    {/* Style Guardrails */}
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <Shield size={14} className="text-violet-400" />
                        <label className={`text-xs font-medium ${textSecondary} uppercase tracking-wider`}>Style Guardrails</label>
                      </div>
                      <textarea value={agentConfig.style_guardrails}
                        onChange={(e) => setAgentConfig(p => ({ ...p, style_guardrails: e.target.value }))}
                        rows={5}
                        className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50 resize-none`}
                        data-testid="wizard-style-guardrails"
                      />
                    </div>

                    {/* Response Guidelines */}
                    <div>
                      <div className="flex items-center gap-2 mb-1.5">
                        <ListChecks size={14} className="text-teal-400" />
                        <label className={`text-xs font-medium ${textSecondary} uppercase tracking-wider`}>Response Guidelines</label>
                      </div>
                      <textarea value={agentConfig.response_guidelines}
                        onChange={(e) => setAgentConfig(p => ({ ...p, response_guidelines: e.target.value }))}
                        rows={5}
                        className={`w-full px-3.5 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50 resize-none`}
                        data-testid="wizard-response-guidelines"
                      />
                    </div>

                    {/* Agent Summary */}
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/[0.02]' : 'bg-gray-50'} border ${cardBorder}`}>
                      <h5 className={`text-xs font-medium ${textSecondary} uppercase tracking-wider mb-3`}>Agent Summary</h5>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Name</p>
                          <p className={`${textPrimary} font-medium`}>{agentConfig.name || 'Not set'}</p>
                        </div>
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Capabilities</p>
                          <div className="flex gap-1 mt-0.5">
                            {agentConfig.voice_enabled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-400 font-medium">Voice</span>}
                            {agentConfig.chat_enabled && <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-400/10 text-teal-400 font-medium">Chat</span>}
                          </div>
                        </div>
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Direction</p>
                          <p className={`${textPrimary} font-medium capitalize`}>{agentConfig.direction}</p>
                        </div>
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Language</p>
                          <p className={`${textPrimary} font-medium`}>{LANGUAGE_OPTIONS.find(l => l.code === agentConfig.language)?.name}</p>
                        </div>
                        {agentConfig.voice_enabled && (
                          <>
                            <div>
                              <p className={`text-[10px] ${textSecondary} uppercase`}>Voice Engine</p>
                              <p className={`${textPrimary} font-medium`}>{agentConfig.voice_engine === 'grok' ? 'Grok by xAI' : 'ElevenLabs'}</p>
                            </div>
                            <div>
                              <p className={`text-[10px] ${textSecondary} uppercase`}>Voice</p>
                              <p className={`${textPrimary} font-medium`}>{agentConfig.voice_engine === 'grok' ? agentConfig.grok_voice : VOICE_OPTIONS.find(v => v.id === agentConfig.voice_id)?.name}</p>
                            </div>
                          </>
                        )}
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Functions</p>
                          <p className={`${textPrimary} font-medium`}>{agentConfig.functions.length} enabled</p>
                        </div>
                        <div>
                          <p className={`text-[10px] ${textSecondary} uppercase`}>Knowledge Base</p>
                          <p className={`${textPrimary} font-medium`}>{agentConfig.knowledge_base_content.trim() ? 'Configured' : 'Empty'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Final Nav */}
                  <div className="flex gap-2 mt-6">
                    <button onClick={() => setWizardStep(4)} className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl ${inputBg} border ${cardBorder} ${textPrimary} text-sm hover:bg-white/10 transition-colors`}>
                      <ChevronLeft size={16} /> Back
                    </button>
                    <button onClick={handleCreateAgent} disabled={isCreating || (!agentConfig.voice_enabled && !agentConfig.chat_enabled)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-teal-400 to-violet-500 text-white text-sm font-medium hover:shadow-lg hover:shadow-teal-400/20 transition-all disabled:opacity-50"
                      data-testid="create-agent-submit-btn"
                    >
                      {isCreating ? <><Loader2 size={16} className="animate-spin" /> Creating...</> : <><Sparkles size={16} /> Create Agent</>}
                    </button>
                  </div>
                </div>
              )}

              </div>{/* end scrollable content */}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Folder Edit Popover */}
      {editingFolder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setEditingFolder(null)}
          data-testid="agent-folder-edit-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-sm rounded-2xl border border-white/[0.08] bg-[#0c0d12] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="agent-folder-edit-modal"
          >
            <div className="px-5 pt-5 pb-3">
              <p className="text-sm font-semibold text-white">Edit Folder</p>
              <p className="text-[11px] text-gray-500">Rename or change the color</p>
            </div>
            <div className="px-5 space-y-4">
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Name</label>
                <input
                  value={editingFolder.name}
                  onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveFolderEdit(); }}
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-teal-400/50"
                  data-testid="agent-folder-edit-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-2 block">Color</label>
                <div className="flex items-center gap-2">
                  {AGENT_FOLDER_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setEditingFolder({ ...editingFolder, color: c })}
                      className={`w-7 h-7 rounded-lg border-2 transition-all ${editingFolder.color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ background: c, boxShadow: editingFolder.color === c ? `0 0 12px ${c}` : 'none' }}
                      data-testid={`agent-folder-edit-color-${c.replace('#','')}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 px-5 py-4 border-t border-white/[0.05] flex items-center justify-between">
              <button
                onClick={() => removeFolder(editingFolder.folder_id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:bg-red-400/10 transition-colors"
                data-testid="agent-folder-edit-delete"
              >
                <Trash2 size={12} /> Delete folder
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingFolder(null)}
                  className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white transition-colors"
                >Cancel</button>
                <button
                  onClick={saveFolderEdit}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-400 text-space-900 hover:bg-teal-500"
                  data-testid="agent-folder-edit-save"
                >Save</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const AIAgentsPage = () => {
  return (
    <SubAccountLayout>
      <AIAgentsContent />
    </SubAccountLayout>
  );
};

export default AIAgentsPage;
